import React, { useEffect, useState } from 'react'

import { LanguageCodes } from '../../../configuration/constants/enums'
import { translateI18n } from '../../../configuration/contexts/I18nContext'
import { useCustomer } from '../../../configuration/hooks/useCustomer'
import getI18nMap from '../../../locales/I18nMap'
import {
  Content,
  Dialog,
  Header,
} from '../../components/dialogs/SharedDialogParts'
import styled, { MQ_TABLET } from '../../constants/theme'
import Modal from '../dialogs/Modal'
import PillButton from '../elements/PillButton'

interface Props {
  children?: React.ReactNode
  chosenLanguageCode?: LanguageCodes
  closeModal: () => void
  handleOnConfirm: () => void
  isModalOpen: boolean
}

export const StaticTextTranslationDialog: React.FC<{
  map: Record<string, any>
}> = ({ map }) => (
  <React.Fragment>
    <MarginTopDiv>
      <BoldText>
        {translateI18n(map, 'preferredLanguageDialog.staticTextDisclaimer')}
      </BoldText>
    </MarginTopDiv>
    <MarginTopDiv>
      {translateI18n(map, 'preferredLanguageDialog.disableDisclaimer')}
    </MarginTopDiv>
    <MarginTopDiv>
      {translateI18n(map, 'preferredLanguageDialog.reloadDisclaimer')}
    </MarginTopDiv>
  </React.Fragment>
)

export const RealTimeTranslationDialog: React.FC<{
  map: Record<string, any>
}> = ({ map }) => (
  <React.Fragment>
    <BoldText>
      {translateI18n(map, 'preferredLanguageDialog.autoTranslateDisclaimer')}
    </BoldText>
    <MarginTopDiv>
      {translateI18n(map, 'preferredLanguageDialog.rttDisclaimer')}
    </MarginTopDiv>
    <MarginTopDiv>
      {translateI18n(map, 'preferredLanguageDialog.thirdPartyDisclaimer')}
    </MarginTopDiv>
  </React.Fragment>
)

export const PreferredLanguageDialog: React.FC<Props> = ({
  children,
  chosenLanguageCode,
  closeModal,
  handleOnConfirm,
  isModalOpen,
}) => {
  const { isRealTimeTranslationEnabled: isRTTEnabled } = useCustomer()
  const [tempLangMap, setTempLangMap] = useState<Record<string, any> | null>(
    null,
  )

  useEffect(() => {
    const setTempLang = async () => {
      const map = await getI18nMap(chosenLanguageCode || LanguageCodes.ENGLISH)
      setTempLangMap(map.default)
    }

    setTempLang()
  }, [chosenLanguageCode])

  return tempLangMap ? (
    <React.Fragment>
      <Modal isOpen={isModalOpen} onRequestClose={closeModal}>
        <Dialog>
          <Header>
            {translateI18n(tempLangMap, 'preferredLanguageDialog.header')}
          </Header>
          <Content>
            {children ? children : null}
            <StyledText isRealTimeTranslation={isRTTEnabled}>
              {isRTTEnabled ? (
                <RealTimeTranslationDialog map={tempLangMap} />
              ) : (
                <StaticTextTranslationDialog map={tempLangMap} />
              )}
            </StyledText>
            <OptionsDiv>
              <PillButton onClick={closeModal} variant="contained">
                {translateI18n(tempLangMap, 'cancel')}
              </PillButton>
              <PillButton onClick={handleOnConfirm} variant="contained">
                {translateI18n(tempLangMap, 'continue')}
              </PillButton>
            </OptionsDiv>
          </Content>
        </Dialog>
      </Modal>
    </React.Fragment>
  ) : null
}

export default PreferredLanguageDialog

const BoldText = styled.div({
  fontWeight: 'bold',
})

export const Label = styled.label({
  clip: 'rect(0 0 0 0)',
  height: 1,
  margin: -1,
  overflow: 'hidden',
  padding: 0,
  position: 'absolute',
  width: 1,
})

const MarginTopDiv = styled.div(({ theme }) => ({
  marginTop: theme.paddingSmall,
}))

const OptionsDiv = styled.div({
  justifyContent: 'flex-end',
  alignItems: 'center',
  ...MQ_TABLET({
    '& > div': {
      marginLeft: 0,
      marginTop: 0,
    },
    display: 'flex',
  }),
  '& > div': {
    marginLeft: -10,
    marginTop: 10,
  },
  '& button': {
    marginLeft: 15,
    width: 90,
  },
  marginTop: 20,
  marginBottom: 10,
})

const StyledText = styled.div<any>(({ isRealTimeTranslation }) => ({
  height: isRealTimeTranslation ? 250 : 150,
}))
